// docs/72 IT19 — a piece's own sounds: `use` when it is drunk, read, thrown or its active fires,
// `equip` when it is put on, `strike` when a magic weapon's blow lands. Each is the catalogue's
// `[bank, variation]` (tools/items/build.mjs hashes the variation the same way for the Rust table),
// so two potions sharing `item.drink` still sound different, and the same potion always the same.
// Inventory actions are client cues (docs/34 SX2: UI sounds are local); in a fight the playback
// calls `playItemSfx` on the item's spell clip.
import { playSfx, type PlayOptions } from '../audio';
import { SFX_KEYS, type SfxKey } from '../audio/keys.gen';
import { catalogueItem, type CatalogueItem, type SfxPick } from './catalogue';

const KNOWN = new Set<string>(SFX_KEYS);
export const isSfxKey = (k: string): k is SfxKey => KNOWN.has(k);

/** The bank and variation a piece plays for `kind`, if it has one the lock knows. */
export function itemSfx(it: CatalogueItem | undefined, kind: 'use' | 'equip' | 'strike'): { key: SfxKey; variant: number } | undefined {
  const pick: SfxPick | undefined = it?.sfx?.[kind];
  if (!pick || !isSfxKey(pick[0])) return undefined;
  return { key: pick[0], variant: pick[1] };
}

/** Plays a catalogue piece's sound. False when it has none (the caller falls back to its old cue). */
export function playItemSfx(def: number | CatalogueItem | undefined, kind: 'use' | 'equip' | 'strike', opts: PlayOptions = {}): boolean {
  const it = typeof def === 'number' ? catalogueItem(def) : def;
  const s = itemSfx(it, kind);
  if (!s) return false;
  return playSfx(s.key, { origin: 'local', ...opts, variant: s.variant });
}
